/* iter-p2-d — Reusable lazy-mounted video overlay.
 *
 * Extracted from `TrainingCenter` so the exact same player is used by
 * (a) Training Center lessons, (b) HelpTip "Watch" button, and
 * (c) Feature Videos admin previews. Only mounted while a URL is set,
 * so YouTube/Loom iframes aren't loaded until the user explicitly
 * clicks Watch.
 */
import React from 'react';
import { PlayCircle, X } from 'lucide-react';

/* Turn any URL into an embeddable player src. */
export function toEmbedUrl(url) {
  if (!url) return { kind: 'none', src: '' };
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      return { kind: 'iframe', src: `https://www.youtube.com/embed/${u.pathname.slice(1)}` };
    }
    if (host.endsWith('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return { kind: 'iframe', src: `https://www.youtube.com/embed/${id}` };
    }
    if (host.endsWith('loom.com') && u.pathname.startsWith('/share/')) {
      return { kind: 'iframe', src: url.replace('/share/', '/embed/') };
    }
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(u.pathname)) {
      return { kind: 'video', src: url };
    }
    return { kind: 'iframe', src: url };
  } catch {
    return { kind: 'none', src: '' };
  }
}

export function VideoOverlay({ title, url, onClose, testIdPrefix = 'video-overlay' }) {
  const embed = toEmbedUrl(url);
  if (!url || !embed.src) return null;
  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-6"
      data-testid={testIdPrefix}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-lg border border-primary/50 bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 bg-neutral-900 px-3 py-2 text-white">
          <div className="flex items-center gap-2 text-[12px]">
            <PlayCircle className="h-4 w-4 text-primary" />
            {title || 'Video'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm p-1 hover:bg-white/10"
            data-testid={`${testIdPrefix}-close`}
            aria-label="Close video"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="aspect-video w-full bg-black">
          {embed.kind === 'iframe' ? (
            <iframe
              src={embed.src}
              title={title || 'Video'}
              className="h-full w-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid={`${testIdPrefix}-iframe`}
            />
          ) : (
            <video
              src={embed.src}
              className="h-full w-full"
              controls
              autoPlay
              data-testid={`${testIdPrefix}-native`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoOverlay;
